import { Button, Input, MediaCard, Modal } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { MediaPreview } from '@sendible/frontendv2/src/components';
import { useComposeBoxContext } from '../../../composeBoxContext';
import { Container, Footer, FooterContent, GridContent, Header, SelectionCount } from '../styles/index.styles';
import { LoadingState } from '../LoadingState';
import { BlankState } from '../BlankState';

export const CommonMediaSearchModal = ({ onClose, icon, label, searchLabel, importButtonLabel, useSearchHook }: MediaSearchModalProps) => {
  const { uploadUrlsToCompose, getRemainingMediaLimit } = useComposeBoxContext();
  const [selectedItems, setSelectedItems] = useState<UrlUploadFileType[]>([]);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const [query, setQuery] = useState('');
  const [querySubmitted, setQuerySubmitted] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const { data: mediaItems, isLoading, isError } = useSearchHook(querySubmitted);
  const { t } = useTranslation(['compose_box', 'common_terms']);
  const currentMaxLimit = getRemainingMediaLimit();

  const mediaList: Media[] | undefined = mediaItems?.map((media) => ({
    url: media.downloadUrl,
    type: media.type,
    id: media.id,
    isGif: media.isGif,
    name: media.title,
    downloadUrl: media.downloadUrl,
  }));

  const handleMediaSelection = (file: UrlUploadFileType) => {
    if (!selectedItems.find((selectedItem) => selectedItem.url === file.url)) {
      setSelectedItems((prev) => [...prev, file]);
    } else {
      const newSelection = selectedItems.filter((i) => i.url !== file.url);

      setSelectedItems(newSelection);
    }
  };

  const handleSubmit = useCallback((value: string) => {
    setQuerySubmitted(value);
    setSelectedItems([]);
  }, []);

  const handleImportClick = useCallback(() => {
    if (selectedItems.length > 0) {
      uploadUrlsToCompose(selectedItems);
      onClose();
    }
  }, [selectedItems, uploadUrlsToCompose, onClose]);

  const handleMediaPreview = (url: string) => {
    const mediaIndex = mediaList?.findIndex((media) => media.url === url);

    if (mediaIndex !== undefined && mediaIndex !== -1) {
      setPreviewInitialItem(mediaIndex);
      setShowPreview(true);
    }
  };

  const handleKeyPress = useCallback(
    (key: string, text: string) => {
      setQuery(text);
      if (key === 'Enter') {
        handleSubmit(text);
      }
    },
    [handleSubmit]
  );

  const subheaderContent = (
    <Header>
      <Input
        icon="search"
        size="sm"
        autoFocus
        label={searchLabel}
        keyPress={handleKeyPress}
        value={query}
        button={{ label: t('search') as string, click: () => handleSubmit(query), disabled: query.trim() === '' }}
        change={(value) => setQuery(value)}
      />
    </Header>
  );

  const buttonLabel = importButtonLabel(selectedItems.length);

  return (
    <>
      <Modal
        portalDomId="layer-root"
        close={onClose}
        headingIcon={icon}
        headingTitle={label}
        preventEscapeClose={showPreview}
        subHeaderContent={subheaderContent}
        variation="large"
        footerContent={
          querySubmitted ? (
            <Footer>
              <FooterContent>
                <SelectionCount>{t('selection_count', { selected: selectedItems.length, limit: currentMaxLimit })}</SelectionCount>
                <Button
                  appearance="primary"
                  disabled={selectedItems.length === 0}
                  label={buttonLabel}
                  onClick={handleImportClick}
                  size={14}
                  variation="fill"
                />
              </FooterContent>
            </Footer>
          ) : undefined
        }
      >
        {querySubmitted && (
          <Container>
            {(!isLoading && mediaItems?.length === 0) || isError ? (
              <BlankState />
            ) : (
              <GridContent>
                {isLoading ? (
                  <LoadingState />
                ) : (
                  mediaItems?.map(({ id, title, thumbUrl, downloadUrl }) => {
                    const isSelected = !!selectedItems.find((selectedItem) => selectedItem.url === downloadUrl);
                    const isDisabled = !isSelected && selectedItems.length >= currentMaxLimit;

                    return (
                      <MediaCard
                        id={id}
                        key={id}
                        name={title}
                        isSelected={isSelected}
                        open={() => handleMediaPreview(downloadUrl)}
                        select={isDisabled ? undefined : () => handleMediaSelection({ name: title, url: downloadUrl })}
                        showCheckboxWithoutHover={isSelected || selectedItems.length > 0}
                        thumbUrl={thumbUrl}
                        type="image"
                        updateDate=""
                        uploadingText=""
                        customClass={isDisabled ? 'disabled-mediacard' : ''}
                      />
                    );
                  })
                )}
              </GridContent>
            )}
          </Container>
        )}
      </Modal>
      {showPreview && mediaList?.length && (
        <MediaPreview
          initialMediaIndex={previewInitialItem}
          mediaList={mediaList}
          onClose={() => setShowPreview(false)}
          shouldShowActions={false}
        />
      )}
    </>
  );
};
